import React from "react";
import { cn } from "@/lib/utils";

export const EntityAvatar = ({ name, theme = "indigo", size = "md", icon: Icon }) => {
  const initials = (name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("") || "?";

  const themes = {
    indigo: "from-indigo-500 to-violet-600 shadow-indigo-500/25",
    sky: "from-sky-500 to-cyan-600 shadow-sky-500/25",
    emerald: "from-emerald-500 to-teal-600 shadow-emerald-500/25",
    rose: "from-rose-500 to-pink-600 shadow-rose-500/25",
    amber: "from-amber-500 to-orange-600 shadow-amber-500/25",
  };

  const sizes = {
    sm: "w-8 h-8 text-[10px]",
    md: "w-10 h-10 text-xs",
    lg: "w-14 h-14 text-base",
  };

  return (
    <div
      className={cn(
        "rounded-xl bg-gradient-to-br text-white font-semibold flex items-center justify-center shadow-lg shrink-0",
        themes[theme] || themes.indigo,
        sizes[size] || sizes.md
      )}
    >
      {Icon ? <Icon className={size === "lg" ? "w-6 h-6" : "w-4 h-4"} /> : initials}
    </div>
  );
};

export const EntityStatPill = ({ label, value, tone = "indigo", isDark }) => {
  const tones = {
    indigo: isDark ? "bg-indigo-500/10 border-indigo-500/25 text-indigo-300" : "bg-indigo-50 border-indigo-200 text-indigo-700",
    sky: isDark ? "bg-sky-500/10 border-sky-500/25 text-sky-300" : "bg-sky-50 border-sky-200 text-sky-700",
    emerald: isDark ? "bg-emerald-500/10 border-emerald-500/25 text-emerald-300" : "bg-emerald-50 border-emerald-200 text-emerald-700",
    violet: isDark ? "bg-violet-500/10 border-violet-500/25 text-violet-300" : "bg-violet-50 border-violet-200 text-violet-700",
  };

  return (
    <div className={cn("rounded-xl border px-3 py-2 min-w-[88px]", tones[tone] || tones.indigo)}>
      <p className="text-[10px] uppercase tracking-wide opacity-80">{label}</p>
      <p className="text-lg font-bold tabular-nums leading-tight">{value}</p>
    </div>
  );
};

export const EntityInfoBlock = ({ title, icon: Icon, tone = "indigo", isDark, children, className }) => {
  const headerTone = {
    indigo: isDark ? "text-indigo-300" : "text-indigo-700",
    sky: isDark ? "text-sky-300" : "text-sky-700",
    emerald: isDark ? "text-emerald-300" : "text-emerald-700",
    amber: isDark ? "text-amber-300" : "text-amber-700",
    rose: isDark ? "text-rose-300" : "text-rose-700",
  };
  const boxTone = {
    indigo: isDark ? "border-indigo-500/20 bg-indigo-500/5" : "border-indigo-100 bg-indigo-50/50",
    sky: isDark ? "border-sky-500/20 bg-sky-500/5" : "border-sky-100 bg-sky-50/50",
    emerald: isDark ? "border-emerald-500/20 bg-emerald-500/5" : "border-emerald-100 bg-emerald-50/50",
    amber: isDark ? "border-amber-500/20 bg-amber-500/5" : "border-amber-100 bg-amber-50/50",
    rose: isDark ? "border-rose-500/20 bg-rose-500/5" : "border-rose-100 bg-rose-50/50",
  };

  return (
    <div className={cn("rounded-xl border p-3", boxTone[tone] || boxTone.indigo, className)}>
      <div className={cn("flex items-center gap-2 text-xs font-semibold mb-2", headerTone[tone] || headerTone.indigo)}>
        {Icon && <Icon className="w-3.5 h-3.5" />}
        <span>{title}</span>
      </div>
      {children}
    </div>
  );
};

export const EntityDetailRow = ({ label, value, isDark, labelClass, textClass, mono = false }) => (
  <div className="flex flex-col sm:flex-row sm:items-start gap-0.5 sm:gap-3 py-1.5 border-b last:border-0 border-dashed border-slate-700/20">
    <span className={cn("text-[11px] sm:w-28 shrink-0", labelClass)}>{label}</span>
    <span className={cn("text-sm break-all", mono && "font-mono text-xs", textClass)}>{value || "—"}</span>
  </div>
);

export const EntityModalHero = ({ name, subtitle, theme = "indigo", icon: Icon, badges = [] }) => {
  const gradients = {
    indigo: "from-indigo-600 to-violet-700",
    sky: "from-sky-600 to-cyan-700",
    emerald: "from-emerald-600 to-teal-700",
  };

  const meta = [subtitle, ...badges].filter(Boolean).join(" · ");

  return (
    <div
      className={cn(
        "relative shrink-0 px-4 py-3 pr-12 bg-gradient-to-r text-white rounded-t-lg border-b border-black/10",
        gradients[theme] || gradients.indigo
      )}
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-white/20 border border-white/25 flex items-center justify-center shrink-0">
          {Icon ? <Icon className="w-[18px] h-[18px]" /> : (
            <span className="text-xs font-bold">
              {(name || "?").slice(0, 2).toUpperCase()}
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1 py-0.5">
          <h3 className="text-[15px] font-semibold leading-snug text-white line-clamp-2">{name}</h3>
          {meta && (
            <p className="text-xs text-white/80 mt-1 leading-normal line-clamp-2">{meta}</p>
          )}
        </div>
      </div>
    </div>
  );
};
